/* eslint-disable default-case */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Context } from '@front10/helpers/dist/Context';
import Box from '../../elements/Box';
import Button from '../../elements/Button';
import BudgetFilter from './BudgetFilter';
import DateSelectorFilter from './DateSelectorFilter';
import SingleDatePicker from '../DatePickerWidget/SingleDatePicker';
import SingleDateRangePicker from '../DatePickerWidget/SingleDateRangePicker';
import AdvancedFilter from '../AdvancedFilter';
import TravelClassFilter from './TravelClassFilter';
import FlightTypeFilter from './FlightTypeFilter';
import { isNotNil, isNilOrEmpty } from '../../../helpers/utils';
import TravelerInterest from './TravelerInterest';
import FlightStopCountFilter from './FlightStopCountFilter';
import RedemptionUnitFilter from './RedemptionFilter';
import CurrencyFilter from './CurrencyFilter';

const SecondaryFilters = (
  {
    filter,
    containerSettings,
    containerFiltersSetting,
    budgetSettings,
    budgetColumnSettings,
    availableFilters,
    dateRange,
    budget,
    travelClass,
    onClearFiltersSecondary,
    clearAllSettings,
    clearAllColumnSettings,
    dateRangeSettings,
    dateRangeColumnSettings,
    travelClassColumnSettings,
    travelClassSettings,
    flightType,
    advanced,
    advancedFiltersSettings,
    advancedColumnSettings,
    flightTypeSettings,
    flightTypeColumnSettings,
    flightStopCount,
    flightStopCountSettings,
    flightStopCountColumnSettings,
    showClearButton = true,
    travelerInterest,
    travelerInterestSettings,
    travelerInterestColumnSettings,
    redemptionUnit,
    redemptionUnitColumnSettings,
    redemptionUnitSettings,
    currency,
    currencyColumnSettings,
    currencySettings,
    locations
    // loading
  },
  ref
) => {
  const { translate } = React.useContext(Context);
  const elementsWrappers = {
    Budget: (e) => (
      <Box key="Budget" marginRight="8px" marginBottom="12px" floatLeft {...budgetColumnSettings}>
        {e}
      </Box>
    ),
    FlightType: (e) => (
      <Box
        key="FlightType"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...flightTypeColumnSettings}
      >
        {e}
      </Box>
    ),
    FlightStopCount: (e) => (
      <Box
        key="FlightStopCount"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...flightStopCountColumnSettings}
      >
        {e}
      </Box>
    ),
    TravelClass: (e) => (
      <Box
        key="TravelClass"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...travelClassColumnSettings}
      >
        {e}
      </Box>
    ),
    DatePicker: (e) => (
      <Box
        key="DatePicker"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...dateRangeColumnSettings}
      >
        {e}
      </Box>
    ),
    DateRangeSelector: (e) => (
      <Box
        key="DatePicker"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...dateRangeColumnSettings}
      >
        {e}
      </Box>
    ),
    AdvancedFilter: (e) => (
      <Box
        key="AdvancedFilter"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...advancedColumnSettings}
      >
        {e}
      </Box>
    ),
    TravelerInterest: (e) => (
      <Box key="TravelerInterest" {...travelerInterestColumnSettings}>
        {e}
      </Box>
    ),
    RedemptionUnit: (e) => (
      <Box
        key="RedemptionUnit"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...redemptionUnitColumnSettings}
      >
        {e}
      </Box>
    ),
    Currency: (e) => (
      <Box
        key="Currency"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...currencyColumnSettings}
      >
        {e}
      </Box>
    )
  };

  const elements = {
    Budget: <BudgetFilter {...budgetSettings} {...budget} />,
    FlightType: <FlightTypeFilter {...flightTypeSettings} {...flightType} />,
    TravelClass: <TravelClassFilter {...travelClassSettings} {...travelClass} />,
    FlightStopCount: <FlightStopCountFilter {...flightStopCountSettings} {...flightStopCount} />,
    DatePicker: (
      <DateSelectorFilter
        ItemComponentInstance={SingleDatePicker}
        dateRangeSettings={dateRangeSettings}
        dateRange={dateRange}
      />
    ),
    DateRangeSelector: (
      <DateSelectorFilter
        ItemComponentInstance={SingleDateRangePicker}
        dateRangeSettings={dateRangeSettings}
        dateRange={dateRange}
      />
    ),
    AdvancedFilter: <AdvancedFilter {...advanced} {...advancedFiltersSettings} />,
    TravelerInterest: <TravelerInterest {...travelerInterest} />,
    RedemptionUnit: <RedemptionUnitFilter {...redemptionUnitSettings} {...redemptionUnit} />,
    Currency: <CurrencyFilter {...currencySettings} {...currency} />
  };

  /**
   * It is necessary to create the payload of only the active filters,
   * because clearAll only affects the secondary filters, never the primary ones.
   *
   * IMPORTANT: As new filters are added, the way in which said
   * filters are cleaned must also be added.
   */
  const handleClear = () => {
    let newPayload = {};
    availableFilters.forEach((filter, index) => {
      let element = filter;
      if (typeof element === 'object' && filter.type) {
        element = filter.type;
      }
      // eslint-disable-next-line default-case
      switch (element) {
        case 'Budget': {
          newPayload.budget = null;
          break;
        }
        case 'DatePicker':
        case 'DateRangeSelector': {
          newPayload.dateRange = {};
          break;
        }
        case 'AdvancedFilter': {
          newPayload.tripDuration = null;
          newPayload.rating = null;
          break;
        }
        case 'TravelerInterest': {
          newPayload.travelerInterest = {};
          if (index === 0) {
            firstElementRef.current.clearInput();
          } else {
            travelerInterestRef.current.clearInput();
          }
          break;
        }
      }
    });

    setTimeout(() => firstElementRef.current?.focusToggleElement(), 100);
    onClearFiltersSecondary(newPayload);
  };

  const filtersChanged = React.useMemo(() => {
    let filtersChange = !!(
      isNotNil(budget.value) ||
      travelClass.isChanged ||
      flightType.isChanged ||
      flightStopCount.isChanged ||
      redemptionUnit.isChanged ||
      currency?.isChanged
    );

    availableFilters.forEach((i) => {
      let item = i;
      if (typeof item === 'object') {
        item = i.type;
      }
      switch (item) {
        case 'DatePicker':
        case 'DateRangeSelector': {
          filtersChange = !!(dateRange.value.start || dateRange.value.end || filtersChange);
          break;
        }
        case 'AdvancedFilter': {
          filtersChange = !!(advanced.tripDuration.value || advanced.rating.value || filtersChange);
          break;
        }
        case 'TravelerInterest': {
          //Validar cuando vengan multiples travelerInterest
          filtersChange = !!(!isNilOrEmpty(travelerInterest?.selectedItems) || filtersChange);
          break;
        }
      }
    });
    /* INFO: 
    To activate the clearAll, it is verified that any of the filters have changed or have no value. The case of the budget is atypical
    because it could be prePopulated and the clearAll would be activated, which is why it is also validated against the origin. In case of including the
    ReducedSecondaryFilter in other components, this comparison should be made optional.
    */
    return Boolean(locations?.origin?.value) && filtersChange;
  }, [
    budget.value,
    travelClass.isChanged,
    flightType.isChanged,
    flightStopCount.isChanged,
    redemptionUnit.isChanged,
    currency?.isChanged,
    availableFilters,
    dateRange.value.start,
    dateRange.value.end,
    advanced?.tripDuration?.value,
    advanced?.rating?.value,
    travelerInterest?.selectedItems,
    locations?.origin?.value
  ]);

  const ClearButton = () => (
    <Box floatLeft {...clearAllColumnSettings}>
      <Button
        onClick={handleClear}
        disabled={!filtersChanged}
        cursor={!filtersChanged ? 'default' : 'pointer'}
        dataTestId="clearAllBtn"
        ariaLabel={translate('cmp-secondary-filter-clear-button-accessibility-message', 'Clear')}
        {...clearAllSettings}
      >
        {translate('cmp-secondary-filter-clear', 'Clear')}
      </Button>
    </Box>
  );

  const filtersToDisplay = availableFilters.filter((e) => {
    let iterator = e;
    if (typeof iterator === 'object') {
      iterator = e.type;
    }
    return elements[iterator];
  });

  const firstElementRef = useRef(null);
  const travelerInterestRef = useRef(null);

  React.useImperativeHandle(ref, () => ({
    focusFirstElement: () => {
      setTimeout(() => firstElementRef.current?.focusToggleElement(), 100);
    }
  }));

  return (
    <Box className="fr-secondary-filters" display="flex" {...containerSettings}>
      <Box display="inline-block" {...containerFiltersSetting}>
        {filtersToDisplay.map((ele, index) => {
          let e = ele;
          if (typeof e === 'object') {
            e = e.type;
          }
          const wrapper = elementsWrappers[e];
          const element = wrapper(elements[e]);

          if (index === 0) {
            if (e === 'TravelerInterest') {
              const temporalElement = React.cloneElement(elements[e], {
                ref: firstElementRef,
                ...travelerInterestSettings[ele.id]
              });
              return wrapper(temporalElement);
            }
            const temporalElement = React.cloneElement(elements[e], { ref: firstElementRef });
            return wrapper(temporalElement);
          } else {
            if (e === 'TravelerInterest') {
              const temporalElement = React.cloneElement(elements[e], {
                ...travelerInterestSettings[ele.id],
                ref: travelerInterestRef
              });
              return wrapper(temporalElement);
            }
            return element;
          }
        })}
        {showClearButton && <ClearButton />}
      </Box>
    </Box>
  );
};

SecondaryFilters.propTypes = {
  showLabels: PropTypes.bool,
  availableFilters: PropTypes.arrayOf(
    PropTypes.oneOf([
      'DateRangeSelector',
      'DatePicker',
      'Passengers/TravelClass',
      'TravelClass',
      'Budget',
      'FlightType',
      'AdvancedFilter',
      'FlightStopCount',
      'RedemptionUnit',
      'Currency',
      PropTypes.objectOf(PropTypes.any)
    ])
  ),
  containerSettings: PropTypes.objectOf(PropTypes.any),
  clearAllSettings: PropTypes.objectOf(PropTypes.any),
  clearAllColumnSettings: PropTypes.objectOf(PropTypes.any),
  travelClassSettings: PropTypes.objectOf(PropTypes.any),
  travelClassColumnSettings: PropTypes.objectOf(PropTypes.any),
  flightTypeSettings: PropTypes.objectOf(PropTypes.any),
  flightTypeColumnSettings: PropTypes.objectOf(PropTypes.any),
  flightStopCountSettings: PropTypes.objectOf(PropTypes.any),
  flightStopCountColumnSettings: PropTypes.objectOf(PropTypes.any),
  redemptionUnitSettings: PropTypes.objectOf(PropTypes.any),
  redemptionUnitColumnSettings: PropTypes.objectOf(PropTypes.any),
  redemptionUnit: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  currencySettings: PropTypes.objectOf(PropTypes.any),
  currencyColumnSettings: PropTypes.objectOf(PropTypes.any),
  currency: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  dateRangeSettings: PropTypes.objectOf(PropTypes.any),
  dateRangeColumnSettings: PropTypes.objectOf(PropTypes.any),
  budgetSettings: PropTypes.objectOf(PropTypes.any),
  budgetColumnSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters state and actions
   */
  dateRange: PropTypes.shape({
    value: PropTypes.shape({
      start: PropTypes.instanceOf(Date),
      end: PropTypes.instanceOf(Date)
    }),
    defaultValue: PropTypes.shape({
      start: PropTypes.instanceOf(Date),
      end: PropTypes.instanceOf(Date)
    }),
    onChange: PropTypes.func,
    onClear: PropTypes.func
  }),
  budget: PropTypes.shape({
    value: PropTypes.number,
    defaultValue: PropTypes.number,
    onChange: PropTypes.func,
    onClear: PropTypes.func,
    currencyCode: PropTypes.string
  }),
  travelClass: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  flightType: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  flightStopCount: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  onClear: PropTypes.func
};

SecondaryFilters.defaultProps = {
  availableFilters: ['Budget', 'DatePicker'],
  showLabels: false,
  containerSettings: {},
  clearAllSettings: {
    color: 'link',
    bgColor: 'transparent',
    textColor: 'light-18',
    borderNone: true
  },
  clearAllColumnSettings: {},
  travelClassSettings: {},
  travelClassColumnSettings: {},
  flightTypeSettings: {},
  flightTypeColumnSettings: {},
  flightStopCountSettings: {},
  flightStopCountColumnSettings: {},
  dateRangeSettings: {},
  dateRangeColumnSettings: {},
  budgetSettings: {},
  budgetColumnSettings: {},
  travelClass: {},
  flightType: {},
  flightStopCount: {},
  redemptionUnitSettings: {},
  redemptionUnitColumnSettings: {},
  redemptionUnit: {},
  dateRange: {},
  budget: {},
  onClear: null
};

export default React.forwardRef(SecondaryFilters);
